if (typeof window !== 'undefined' && typeof window.queueMicrotask !== 'function') {
  (window as any).queueMicrotask = function (callback: () => void) {
    Promise.resolve().then(callback).catch((err) => setTimeout(() => { throw err; }, 0));
  };
}

if (typeof Promise !== 'undefined' && !(Promise.prototype as any).finally) {
  (Promise.prototype as any).finally = function (callback: any) {
    const constructor = this.constructor;
    return this.then(
      (value: any) => constructor.resolve(callback()).then(() => value),
      (reason: any) => constructor.resolve(callback()).then(() => { throw reason; })
    );
  };
}

if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'undefined') {
  class SimpleResizeObserver {
    callback: any;
    constructor(callback: any) {
      this.callback = callback;
    }
    observe() {}
    unobserve() {}
    disconnect() {}
  }
  (window as any).ResizeObserver = SimpleResizeObserver;
}

if (typeof window !== 'undefined' && typeof (window as any).AbortController === 'undefined') {
  class SimpleAbortSignal {
    aborted = false;
    onabort: any = null;
    addEventListener() {}
    removeEventListener() {}
    dispatchEvent() { return true; }
  }
  class SimpleAbortController {
    signal = new SimpleAbortSignal();
    abort() {
      this.signal.aborted = true;
      if (typeof this.signal.onabort === 'function') {
        this.signal.onabort();
      }
    }
  }
  (window as any).AbortController = SimpleAbortController;
  (window as any).AbortSignal = SimpleAbortSignal;
}

import React, { useEffect, useState } from 'react';
import { createRootRouteWithContext, Outlet, ScrollRestoration } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { Toaster } from 'react-hot-toast';
import { useIsMobile } from '@/utils/hooks/use-mobile';
import { formatFunc } from '@/utils/string';
import MissiAiWidget from '@/components/ui/ai-chat-widget/MissiAiWidget';
import type { QueryClient } from '@tanstack/react-query';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'tiny-slider/dist/tiny-slider.css';
import '@/styles/globals.css';

String.prototype.format = function (data) {
  // @ts-ignore
  return formatFunc(this, data);
};

export interface RouterContext {
  queryClient: QueryClient;
}

function PerfMonitor() {
  const [fps, setFps] = useState(60);
  const [ram, setRam] = useState(0);
  const [frameLoad, setFrameLoad] = useState(0);
  const [gpuName, setGpuName] = useState<string>('');

  useEffect(() => {
    try {
      const canvas = document.createElement('canvas');
      const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as any;
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
          if (renderer) {
            const clean = renderer.replace(/^ANGLE \([^,]+,\s*/, '').replace(/,?\s*Direct3D.*/, '').replace(/\)/g, '').trim();
            setGpuName(clean);
          }
        }
        const lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    let frameCount = 0;
    let lastTime = performance.now();
    let animId: number;

    const tick = () => {
      const now = performance.now();
      const delta = now - lastTime;
      frameCount++;

      if (delta >= 1000) {
        const currentFps = Math.round((frameCount * 1000) / delta);
        const avgFrameMs = delta / frameCount;
        const loadPct = Math.round((avgFrameMs / 16.67) * 100);

        setFps(currentFps);
        setFrameLoad(loadPct);
        frameCount = 0;
        lastTime = now;

        if ((performance as any).memory) {
          const used = (performance as any).memory.usedJSHeapSize / (1024 * 1024);
          setRam(Math.round(used));
        }
      }

      animId = requestAnimationFrame(tick);
    };

    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <div className="fixed top-3 right-3 z-[99999] bg-slate-900/90 text-white font-mono text-[11px] px-3 py-1.5 rounded-lg shadow-xl border border-white/20 backdrop-blur pointer-events-none select-none flex flex-col gap-1">
      <div className="flex items-center gap-3">
        <div>FPS: <span className={fps < 30 ? 'text-red-400 font-bold' : 'text-emerald-400 font-bold'}>{fps}</span></div>
        <div>LOAD: <span className={frameLoad > 100 ? 'text-red-400 font-bold' : frameLoad > 75 ? 'text-amber-300 font-bold' : 'text-emerald-400 font-bold'}>{frameLoad}%</span></div>
        {ram > 0 && <div>RAM: <span className="text-sky-400 font-bold">{ram} MB</span></div>}
      </div>
      {gpuName && <div className="text-[9px] text-slate-400 truncate max-w-[240px]">GPU: {gpuName}</div>}
    </div>
  );
}

function RootComponent() {
  const isMobile = useIsMobile();
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_VCONSOLE === 'true') {
      setShowDebug(true);
      // @ts-ignore
      import('vconsole').then((VConsole) => {
        new VConsole.default();
      });
    }
  }, []);

  return (
    <>
      <ScrollRestoration />
      <Outlet />
      <MissiAiWidget />
      {showDebug && <PerfMonitor />}
      <Toaster
        position={isMobile ? 'top-center' : 'bottom-left'}
        containerStyle={{
          zIndex: 9999999,
        }}
        containerClassName="react-hot-toast-container"
        toastOptions={{
          className: 'react-hot-toast',
          duration: 4000,
        }}
      />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: () => (
    <div className="flex flex-col items-center justify-center min-h-screen text-slate-700 dark:text-slate-200">
      <h1 className="text-6xl font-bold mb-4">404</h1>
      <p className="text-xl mb-6">Halaman tidak ditemukan</p>
      <a href="/" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
        Kembali ke Beranda
      </a>
    </div>
  ),
});
